import React, {useState} from 'react'

import {useFormik} from 'formik'
import * as Yup from 'yup'

import config from '../../../config'

import {useAppDispatch} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import {HttpStatusError} from '../../../../../commons/src/libs/http-json'
import {maxLength} from '../../../../../commons/src/libs/validator'
import {
  captureException,
  captureIgnoredException,
} from '../../../../../commons/src/services/logging'
import {formatCurrency, formatPrice} from '../../../libs/formatter'
import {calculatePricesForAdditionalCost} from '../../../libs/pricing'
import {postAdditionalCostRequest} from '../../../services/api/partner'
import {toNumberValue} from '../../utils/form'

import {ModalMeta, ProviderId} from '../../../types'

import sendIcon from '../../../../../commons/src/assets/icons/send.svg'

import Button from '../../../../../commons/src/components/button'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline/headline'
import Icon from '../../../../../commons/src/components/icon'
import LoadingIndicator from '../../../../../commons/src/components/loading-indicator'
import Modal from '../../../../../commons/src/components/modal'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'
import Stack from '../../../../../commons/src/components/stack'
import SuccessMessage from '../../../../../commons/src/components/success-message'
import TextField from '../../../../../commons/src/components/text-field'

import {Order} from '../../../order-types'

class AdditionalCostRequestError extends Error {
  constructor(message?: string) {
    super(message || `Additional costs request failed`)
    this.name = 'AdditionalCostRequestError'
  }
}

const getErrorMessage = (error: unknown): string | null => {
  try {
    if (error && error instanceof HttpStatusError && error.body) {
      return JSON.parse(error.body).error?.message[0]
    }
    return null
  } catch (e) {
    return null
  }
}

const validationSchema = {
  net: Yup.number().moreThan(0).label('Additional cost value').required(),
  description: Yup.string().label('Description').required(),
}

type Props = {
  meta: ModalMeta
  order: Order
  providerId: ProviderId
  onDone: () => Promise<void>
}

const AdditionalCostModal = ({meta, order, providerId, onDone}: Props) => {
  const [isSendingRequest, setIsSendingRequest] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const dispatch = useAppDispatch()

  const formik = useFormik({
    initialValues: {
      net: '',
      description: '',
      formikGeneral: '',
    },
    validationSchema: Yup.object(validationSchema),
    onSubmit: async (values, {setFieldError}) => {
      const netValue = toNumberValue(values.net)

      if (!maxLength(config.additionalCost.descriptionMaxLength)(values.description)) {
        setFieldError(
          'formikGeneral',
          `Your message length exceeds the maximum of ${config.additionalCost.descriptionMaxLength} chars.`
        )
      } else if (netValue === null) {
        setFieldError('formikGeneral', `The additional cost value must be a number.`)
      } else {
        setIsSendingRequest(true)
        try {
          await postAdditionalCostRequest(order.orderId, {
            net: netValue,
            description: values.description,
          })
          await onDone()

          setIsSubmitted(true)
        } catch (error) {
          const errorMessage = getErrorMessage(error)
          if (error && error instanceof HttpStatusError && error.response.status === 401) {
            captureIgnoredException(error)
          } else {
            captureException(
              new AdditionalCostRequestError(errorMessage || (error as Error)?.message),
              'default',
              {
                originalError: error,
                providerId,
                additionalCostValue: values.net,
                additionalCostDescription: values.description,
              }
            )
          }
          setFieldError(
            'formikGeneral',
            errorMessage || 'Oops something went wrong. Please try again!'
          )
        } finally {
          setIsSendingRequest(false)
        }
      }
    },
  })

  const pricings = calculatePricesForAdditionalCost(
    formik.values.net ? parseFloat(formik.values.net as string) : 0,
    config.priceCalculation.craftcloudMargin
  )

  const ccMarginOnAdditionalCostExceptionApplies = config.ccMarginOnAdditionalCostExceptions.some(
    exception => formik.values.description === exception
  )

  const finalCustomerPrice = formatPrice(pricings.totalCustomerPriceV2, order.customer.currency)

  const yourEarningsAmount = ccMarginOnAdditionalCostExceptionApplies
    ? formatPrice(pricings.totalCustomerPriceV2, order.customer.currency)
    : formatPrice(pricings.invoiceableAmountV2, order.customer.currency)

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Request additional costs" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        isSubmitted ? (
          <Button key="close" onClick={() => dispatch(closeModal())}>
            Close
          </Button>
        ) : (
          <>
            <Button
              key="cancel"
              hierarchy="ghost"
              onClick={() => dispatch(closeModal())}
              disabled={isSendingRequest}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              disabled={formik.isSubmitting || !formik.isValid}
              onClick={() => formik.handleSubmit()}
              icon={isSendingRequest ? <LoadingIndicator invert /> : <Icon source={sendIcon} />}
              iconAlign="right"
            >
              Submit
            </Button>
          </>
        )
      }
    >
      {isSubmitted && <SuccessMessage>The request was sent successfully</SuccessMessage>}
      {!isSubmitted && (
        <>
          <Paragraph size="s">
            Here you can request additional costs for an order. The additional costs can be related
            to changes to the order in terms of material change, added services and/or shipping
            adjustments.
          </Paragraph>
          <Paragraph size="s">
            In the description section only state the service you are offering for the additional
            cost -{' '}
            <strong>do not use this field to describe why the order needs additional costs</strong>.
            This should already be clarified with the customer before proceeding to request these
            additional costs. Also, note that the information added in the description section will
            show up on the invoice - so keep it short and easy to determine what the additional
            costs were for.
          </Paragraph>
          <Paragraph size="s">
            <strong>Please note:</strong> The additional cost amount you enter will include our 25%
            margin, please calculate accordingly.
          </Paragraph>
          <Paragraph size="s">
            The customer will receive a payment link and you will be notified about the successful
            payment, allowing you to proceed with the order.
          </Paragraph>
          <Paragraph size="s" minor className="u-margin-bottom-l">
            <strong>Note:</strong> We recommend that you await the successful payment before moving
            forward with the order - Craftcloud will not be covering any services fulfilled for
            orders where the additional costs have not been paid.
          </Paragraph>
          <form onSubmit={formik.handleSubmit}>
            <Stack>
              <Stack space="80">
                <Headline as="h2" label="Amount" size="s" className="u-no-margin-bottom" />
                <FormRow layout="s-l">
                  <NumberInputField
                    allowDecimals
                    allowOnly2Decimals
                    name="net"
                    value={formik.values.net}
                    suffix={formatCurrency(order.customer.currency)}
                    appearance={formik.submitCount > 0 && !!formik.errors.net ? 'error' : undefined}
                    onChange={(value, name) => formik.setFieldValue(name as string, value)}
                    onBlur={formik.handleBlur}
                    disabled={isSendingRequest || isSubmitted}
                  />
                  <Stack space="60">
                    <Paragraph minor size="xs" className="u-no-margin-bottom">
                      Final Customer price: {formik.values.net ? finalCustomerPrice : 'n/a'}
                    </Paragraph>
                    <Paragraph minor size="xs" className="u-no-margin-bottom">
                      Your earnings: {formik.values.net ? yourEarningsAmount : 'n/a'}
                    </Paragraph>
                  </Stack>
                </FormRow>
                <Paragraph minor size="xs" className="u-no-margin-bottom">
                  <strong>Note: </strong>Amount entered is in the customer&apos;s currency.
                </Paragraph>
              </Stack>
              <Stack space="80">
                <Headline as="h2" label="Description" size="s" className="u-no-margin-bottom" />
                <FormRow>
                  <TextField
                    placeholder="Describe the purpose of the additional cost"
                    name="description"
                    rows={5}
                    maxLength={config.additionalCost.descriptionMaxLength}
                    appearance={
                      formik.submitCount > 0 && !!formik.errors.description ? 'error' : undefined
                    }
                    onChange={(v, n, event) => formik.handleChange(event)}
                    value={formik.values.description}
                    onBlur={formik.handleBlur}
                    disabled={isSendingRequest || isSubmitted}
                  />
                </FormRow>
              </Stack>
            </Stack>
          </form>
          {formik.errors.formikGeneral && (
            <Paragraph size="s" error>
              {formik.errors.formikGeneral}
            </Paragraph>
          )}
        </>
      )}
    </Modal>
  )
}

export default AdditionalCostModal
